import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import NoiseMatrixEditor from '@/components/spots-admin/NoiseMatrixEditor';
import type { NoiseMatrix } from '@/utils/noiseMatrix';

const CATEGORIES = [
  { label: 'Café', value: 'cafe' },
  { label: 'Gym', value: 'gym' },
  { label: 'Hotel Lobby', value: 'hotel_lobby' },
  { label: 'Coworking', value: 'coworking' },
  { label: 'Library', value: 'library' },
  { label: 'Restaurant', value: 'restaurant' },
  { label: 'Pod', value: 'pod' },
  { label: 'Other', value: 'other' },
] as const;

const ACCESS_TYPES = [
  { label: 'Free', value: 'free' },
  { label: 'Purchase Required', value: 'purchase_required' },
  { label: 'Members Only', value: 'members_only' },
] as const;

export interface ApprovePayload {
  status: 'approved';
  category: string;
  access_type: string;
  noise_matrix?: NoiseMatrix;
  description?: string;
  admin_notes?: string;
}

interface ApproveModalProps {
  placeName: string;
  onConfirm: (payload: ApprovePayload) => Promise<{ ok: boolean; error?: string }>;
  onClose: () => void;
}

const inputClass = 'w-full px-3 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none';

export default function ApproveModal({ placeName, onConfirm, onClose }: ApproveModalProps) {
  const [category, setCategory] = useState('');
  const [accessType, setAccessType] = useState('');
  const [noiseMatrix, setNoiseMatrix] = useState<NoiseMatrix | null>(null);
  const [description, setDescription] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ category?: boolean; accessType?: boolean }>({});

  const handleSubmit = async () => {
    const newErrors = {
      category: !category,
      accessType: !accessType,
    };
    setErrors(newErrors);
    if (newErrors.category || newErrors.accessType) return;

    setLoading(true);
    setApiError(null);

    const payload: ApprovePayload = {
      status: 'approved',
      category,
      access_type: accessType,
      ...(noiseMatrix && { noise_matrix: noiseMatrix }),
      ...(description.trim() && { description: description.trim() }),
      ...(adminNotes.trim() && { admin_notes: adminNotes.trim() }),
    };

    const result = await onConfirm(payload);
    setLoading(false);
    if (!result.ok) {
      setApiError(result.error || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 px-4" onClick={onClose}>
      <div className="bg-card border border-input rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-foreground">Approve Suggestion</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X size={20} /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Approving: <span className="font-medium text-foreground">{placeName}</span></p>

        <div className="space-y-5">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Category <span className="text-destructive">*</span></label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => { setCategory(c.value); setErrors(e => ({ ...e, category: false })); }}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                    category === c.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {errors.category && <p className="text-xs text-destructive mt-1.5">Please select a category</p>}
          </div>

          {/* Access Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Access Type <span className="text-destructive">*</span></label>
            <div className="flex flex-wrap gap-2">
              {ACCESS_TYPES.map(a => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => { setAccessType(a.value); setErrors(e => ({ ...e, accessType: false })); }}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                    accessType === a.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-input text-muted-foreground hover:text-foreground hover:bg-muted',
                  )}
                >
                  {a.label}
                </button>
              ))}
            </div>
            {errors.accessType && <p className="text-xs text-destructive mt-1.5">Please select an access type</p>}
          </div>

          {/* Noise Matrix */}
          <NoiseMatrixEditor value={noiseMatrix} onChange={setNoiseMatrix} />

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short editorial description shown to users…"
              rows={3}
              className={inputClass}
            />
          </div>

          {/* Admin Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Admin Notes</label>
            <textarea
              value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="Internal notes, not shown to users…"
              rows={2}
              className={inputClass}
            />
          </div>
        </div>

        {/* API error */}
        {apiError && (
          <div className="mt-4 bg-destructive/10 text-destructive text-sm rounded-xl px-4 py-3">{apiError}</div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg border border-input text-muted-foreground hover:text-foreground">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-success text-primary-foreground disabled:opacity-50 flex items-center gap-1.5 hover:opacity-90"
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Confirm Approval
          </button>
        </div>
      </div>
    </div>
  );
}
